import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { nav } from "../mock";
import BaseLogo from "./BaseLogo";

const Logo = ({ dark }) => (
  <Link to="/" className="flex items-center group">
    <BaseLogo dark={dark} size={26} />
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Pages with a dark hero at the top => start transparent/white text
  const darkHeroRoutes = ["/", "/projects", "/team", "/testimonials"];
  const hasDarkHero = darkHeroRoutes.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // dark = dark text on light nav bar
  const darkText = scrolled || !hasDarkHero;

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 200);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <>
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        darkText
          ? "bg-[#e9e4dc]/95 backdrop-blur-sm shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
        <Logo dark={darkText} />

        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {nav.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNav(item.to)}
              className={`text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 hover:opacity-60 ${
                darkText ? "text-[#0f0f10]" : "text-white"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0 })}
            className="hidden md:inline-flex items-center px-6 py-3 text-[11px] tracking-[0.22em] uppercase text-white transition-all duration-300 hover:brightness-110"
            style={{ backgroundColor: "#E97B2B" }}
          >
            Get in Touch
          </Link>

          <button
            className={`lg:hidden ${darkText ? "text-[#0f0f10]" : "text-white"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </header>

      {/* Mobile full-screen menu */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-[70] flex flex-col"
          style={{ backgroundColor: "#0f0f10" }}
        >
          <div className="max-w-[1500px] w-full mx-auto px-6 h-[68px] flex items-center justify-between">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center">
              <BaseLogo dark={false} size={26} />
            </Link>
            <button
              className="text-white"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <X size={26} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col items-center justify-center gap-9">
            <button
              onClick={() => handleNav("/")}
              className="text-white text-[24px] tracking-[0.14em] uppercase font-light hover:opacity-60 transition-opacity"
            >
              Home
            </button>
            {nav.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNav(item.to)}
                className="text-white text-[24px] tracking-[0.14em] uppercase font-light hover:opacity-60 transition-opacity"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center px-10 py-4 text-[11px] tracking-[0.24em] uppercase text-white transition-all duration-300 hover:brightness-110"
              style={{ backgroundColor: "#E97B2B" }}
            >
              Get in Touch
            </Link>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
